import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Popover, Row, Space, Typography, theme } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldRequirement, FieldRequirementTypeMap } from '../../model/types';
import { getRequiredErrorText, getRequiredStatusErrorText } from '../../utils/forms/formHelper.validators';
import uuid from '../../utils/uuid';

const { Text } = Typography;

export interface GrispiLabelProps {
    actionButton?: ReactNode;
    description?: string;
    label?: ReactNode;
    fieldRequirement?: FieldRequirement;
}

const styles = {
    text: { display: 'block' },
    tooltip: { maxWidth: 300 },
    icon: { color: 'GrayText' }
};

function getRequirementIcon(fieldRequirement: FieldRequirement) {
    switch (fieldRequirement?.type) {
        case FieldRequirementTypeMap.TRUE:
            return '* ';
        case FieldRequirementTypeMap.TARGET_STATUS:
            return '⚐ ';
        default:
            null;
    }
}

function getRequirementText(fieldRequirement: FieldRequirement) {
    switch (fieldRequirement?.type) {
        case FieldRequirementTypeMap.TRUE:
            return [getRequiredErrorText()];
        case FieldRequirementTypeMap.TARGET_STATUS:
            return fieldRequirement.ids.map(getRequiredStatusErrorText);
        default:
            null;
    }
}

function Content({ fieldRequirement }: { fieldRequirement: FieldRequirement }) {
    return (
        <Space direction="vertical" className="full-width">
            {getRequirementText(fieldRequirement)?.map((text) => (
                <Text type="secondary" key={uuid()}>
                    {text}
                </Text>
            ))}
        </Space>
    );
}

const GrispiLabel = (props: GrispiLabelProps) => {
    const {
        token: { fontSize }
    } = theme.useToken();
    const { t } = useTranslation('forms');
    if (Object.keys(props).length === 0) return null;

    const { actionButton, description, label, fieldRequirement } = props;

    return (
        <Row justify="space-between" align="middle">
            <Col flex={1}>
                <Space>
                    {typeof label === 'string' ? (
                        <strong style={{ ...styles.text, fontSize: fontSize - 1 }}>
                            <Popover
                                placement="right"
                                content={<Content fieldRequirement={fieldRequirement} />}
                                trigger="hover"
                                overlayStyle={{ ...styles.tooltip, fontSize: fontSize - 1 }}
                            >
                                <span style={{ color: 'red' }}>{getRequirementIcon(fieldRequirement)}</span>
                            </Popover>

                            {t(label as any)}
                        </strong>
                    ) : (
                        label
                    )}
                    {description && (
                        <Popover
                            placement="right"
                            content={<Text type="secondary">{description}</Text>}
                            trigger="hover"
                            overlayStyle={{ ...styles.tooltip, fontSize: fontSize - 1 }}
                        >
                            <InfoCircleOutlined style={{ ...styles.icon, fontSize: fontSize - 1 }} />
                        </Popover>
                    )}
                </Space>
            </Col>
            {actionButton && <Col>{actionButton}</Col>}
        </Row>
    );
};

export default GrispiLabel;
